import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, MessageCircle } from "lucide-react";
import api from "@/lib/api";

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enquiries");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const markReviewed = async (id) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, null, { params: { status: "reviewed" } });
      toast.success("Marked reviewed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div>
        <span className="kbd-eyebrow">Admin</span>
        <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Custom Quote Enquiries</h1>
      </div>

      <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
        {loading && <div className="rounded-2xl border border-border/70 bg-card p-6 text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border/70 bg-card p-10 text-center text-sm text-muted-foreground" data-testid="admin-enquiries-empty">
            No enquiries yet.
          </div>
        )}
        {items.map((e) => {
          const waMatch = /WhatsApp[^\n]*\(to ([^)]+)\)/.exec(e.special_requirements || "");
          const pricingItem = e.selected_menu?.find((m) => m.kind === "pricing");
          return (
          <div key={e.id} className="rounded-2xl border border-border/70 bg-card p-4 sm:p-5" data-testid={`admin-enquiry-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-words font-serif-display text-lg font-semibold text-secondary sm:text-xl">{e.customer_name}</span>
                  {waMatch && (
                    <span
                      data-testid={`admin-enquiry-whatsapp-badge-${e.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#25D366]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#128C4B]"
                    >
                      <MessageCircle className="h-3 w-3" /> WhatsApp · {waMatch[1].trim()}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {e.mobile}</span>
                  {e.whatsapp && <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {e.whatsapp}</span>}
                  {e.email && <span className="inline-flex min-w-0 items-center gap-1"><Mail className="h-3 w-3 shrink-0" /> <span className="break-all">{e.email}</span></span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {e.customer_id && (
                  <Link
                    data-testid={`admin-enquiry-quote-link-${e.id}`}
                    to={`/admin/quotes/${e.id}`}
                    className="inline-flex min-h-[32px] items-center text-xs font-semibold text-primary hover:underline"
                  >
                    Open in Quotes →
                  </Link>
                )}
                <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${e.status === "new" ? "bg-accent/20 text-secondary" : "bg-muted text-muted-foreground"}`}>
                  {e.status}
                </span>
                {e.status === "new" && (
                  <button data-testid={`admin-enquiry-mark-reviewed-${e.id}`} onClick={() => markReviewed(e.id)} className="btn-ghost !py-2 !text-xs sm:!py-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark reviewed
                  </button>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm lg:grid-cols-5">
              <Meta label="Event">{e.event_type || "—"}</Meta>
              <Meta label="Guests">{e.guest_count || "—"}</Meta>
              <Meta label="Date/Time">{[e.event_date, e.event_time].filter(Boolean).join(" · ") || "—"}</Meta>
              <Meta label="Venue">{e.venue || "—"}</Meta>
              <Meta label="Estimated Amount">
                <span data-testid={`admin-enquiry-estimate-${e.id}`}>
                  {e.estimated_amount != null
                    ? `₹${Number(e.estimated_amount).toLocaleString("en-IN")}`
                    : pricingItem?.subtotal != null
                    ? `₹${Number(pricingItem.subtotal).toLocaleString("en-IN")}`
                    : pricingItem?.subtotal_partial != null
                    ? `Partial · ₹${Number(pricingItem.subtotal_partial).toLocaleString("en-IN")}`
                    : "—"}
                </span>
              </Meta>
            </div>
            <div className="mt-4 rounded-xl bg-muted/50 p-3 text-sm" data-testid="enquiry-special-requirements">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Special requirements</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-secondary">
                {e.special_requirements || "Not specified"}
              </div>
            </div>
            <MenuTags e={e} />
            <div className="mt-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Received {new Date(e.created_at).toLocaleString()}</div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function MenuTags({ e }) {
  // selected_menu (guest/WhatsApp path) first; snapshot fallback for structured quotes
  const items = e.selected_menu?.length
    ? e.selected_menu.filter((m) => m.kind !== "pricing")
    : [
        ...(e.package_snapshot?.name ? [{ name: `${e.package_snapshot.name} (package)` }] : []),
        ...(e.dishes_snapshot || []).map((d) => ({ name: d.name })),
        ...(e.extras_snapshot || []).map((x) => ({
          name: x.category_name ? `Extra: ${x.category_name}` : "Extra",
          qty: x.qty,
        })),
      ];
  if (!items.length) {
    return (
      <div className="mt-4">
        <span
          data-testid={`admin-enquiry-no-menu-${e.id}`}
          className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-red-800"
        >
          No menu selected
        </span>
      </div>
    );
  }
  return (
    <div className="mt-4" data-testid={`admin-enquiry-menu-${e.id}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Selected dishes</div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {items.map((m, i) => (
          <li key={i} className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs">
            {m.name}
            {m.qty ? ` ×${m.qty}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 break-words font-medium text-secondary">{children}</div>
    </div>
  );
}
